import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { playTick } from "@/components/site/Nav";

const LINES = ["WE CREATE ADS.", "WE TEST THEM.", "WE SCALE", "WHAT WORKS."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.45 } },
};

const maskedLine = {
  hidden: { y: "112%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero({ onOpenConcierge }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const wordmarkY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="section-hero"
      className="relative flex min-h-screen flex-col justify-between overflow-hidden pt-28 sm:pt-32"
    >
      <div className="grid items-end gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:gap-8 lg:px-14">
        <motion.div variants={container} initial="hidden" animate="show" className="lg:col-span-7">
          <h1
            data-testid="hero-title"
            className="font-display text-[12.5vw] font-extrabold uppercase leading-[0.94] tracking-tighter sm:text-[8.5vw] lg:text-[3.9vw]"
          >
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-[0.05em]">
                <motion.span
                  variants={maskedLine}
                  className={`block will-change-transform ${
                    i === 0 ? "text-white/80" : i === 1 ? "hatch-text" : ""
                  }`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-6">
            <p data-testid="hero-supporting-line" className="text-sm text-zinc-400">
              Meta Advertising · Creative Strategy · AI Content Studio
            </p>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imgY }} className="hero-visual relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-image-frame"
            className="relative overflow-hidden"
          >
            <motion.img
              initial={{ scale: 1.18 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              src="/assets/hero-master.webp"
              alt="Gentle and Monster — the two studio characters"
              className="block h-auto w-full"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 z-10 flex" aria-hidden="true">
              <div data-testid="hero-zone-gentle" onPointerEnter={playTick} className="hero-zone-gentle group/gentle relative h-full flex-1">
                <div
                  data-testid="hero-bubble-gentle"
                  className="pointer-events-none absolute left-[6%] top-[3%] rounded-[4px] border border-white/25 bg-black px-2.5 py-1 opacity-0 transition-opacity duration-200 group-hover/gentle:opacity-100"
                >
                  <p className="whitespace-nowrap font-monoalt text-[8px] uppercase leading-normal tracking-[0.2em] text-white">
                    Hi! I&apos;m Gentle.
                  </p>
                  <span className="absolute -bottom-[3px] right-4 h-[5.5px] w-[5.5px] rotate-45 bg-black" />
                </div>
              </div>
              <div data-testid="hero-zone-monster" onPointerEnter={playTick} className="hero-zone-monster group/monster relative h-full flex-1">
                <div
                  data-testid="hero-bubble-monster"
                  className="pointer-events-none absolute right-[6%] top-[16%] rounded-[4px] bg-white px-2.5 py-1 opacity-0 transition-opacity duration-200 group-hover/monster:opacity-100"
                >
                  <p className="whitespace-nowrap font-monoalt text-[8px] uppercase leading-normal tracking-[0.2em] text-black">
                    Hi! I&apos;m Monster.
                  </p>
                  <span className="absolute -bottom-[3px] right-4 h-[5.5px] w-[5.5px] rotate-45 bg-white" />
                </div>
              </div>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            className="mt-[1px] translate-x-[7.9349%] font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400"
          >
            <span className="font-medium">Gentle</span> — <span className="cap-gentle">Human Decision</span> /{" "}
            <span className="font-medium">Monster</span> — <span className="cap-monster">AI Power</span>
          </motion.p>
        </motion.div>
      </div>

      <div className="px-6 py-24 sm:px-10 sm:py-32 lg:px-14" data-testid="intro-block">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-lg font-monoalt text-[13px] font-normal leading-[2.1] tracking-[0.06em] text-[#8E97A6]"
        >
          Gentle&amp;Monster is a{" "}
          <span className="text-[#B7BFCD]">creative and performance agency</span> founded by Alex de
          Selve and Antoine Eole. Coming from creative backgrounds — design, imagery, 3D AI, video,
          music and sound design — we bring this artistic culture to Meta Ads strategy and brand
          growth. Our approach brings together creative sensitivity, media buying and performance
          analysis: understanding what captures attention, testing what converts, and turning{" "}
          <span className="text-[#B7BFCD]">creativity into a driver of growth</span>.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex max-w-lg justify-end"
        >
          <button
            type="button"
            onClick={onOpenConcierge}
            data-testid="intro-cta"
            className="hatch-cta inline-flex cursor-pointer items-center gap-2.5 px-6 py-3.5 font-monoalt text-[11px] font-normal uppercase tracking-[0.25em] text-zinc-400"
          >
            Let&apos;s Talk
            <span className="text-zinc-400">+</span>
          </button>
        </motion.div>
      </div>

      <motion.div style={{ y: wordmarkY }} className="overflow-hidden" data-testid="hero-wordmark">
        <motion.svg
          initial={{ y: "100%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.2, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
          viewBox="0 0 1000 82"
          preserveAspectRatio="xMinYMid meet"
          className="block w-full select-none px-6 text-white sm:px-10 lg:px-14"
          role="img"
          aria-label="Gentle&Monster"
        >
          <text
            x="0"
            y="60"
            textLength="1000"
            lengthAdjust="spacingAndGlyphs"
            style={{ fontFamily: "Syne, 'Avant Garde', 'Helvetica Neue', sans-serif", fontWeight: 800, fontSize: 62, letterSpacing: "-0.02em" }}
            fill="currentColor"
          >
            GENTLE&amp;MONSTER
          </text>
        </motion.svg>
      </motion.div>
    </section>
  );
}
